import { postData } from "../../shared/utils";

export function snapshotResponse(response, commentsExpanded = false) {
  const { liked, like_count, text, text_visible_to, comments } = response;

  const baseUrl = `/snapshot_response/${response.id}`;

  let originalText, originalTextVisibleTo, originalCommentText;

  return {
    liked,
    like_count,
    text,
    text_visible_to,
    response,
    comments,
    commentsExpanded,
    creatingComment: false,
    responseContextMenuExpanded: false,
    editingResponse: false,
    commentContextMenuExpanded: false,
    editingComment: false,
    userCache: new Map(),
    async init() {
      // Hydrate user cache for all mentions in comments
      await this.hydrateUserCache();
    },
    async hydrateUserCache() {
      if (!window.parseMentionEmails || !window.userSearch) {
        return;
      }

      // Extract all mention emails from response text and all comments
      const allEmails = new Set();

      // Extract from response text
      if (this.text) {
        const emails = window.parseMentionEmails(this.text);
        emails.forEach((email) => allEmails.add(email));
      }

      // Extract from comments
      this.comments.forEach((comment) => {
        const emails = window.parseMentionEmails(comment.text);
        emails.forEach((email) => allEmails.add(email));
      });

      // Fetch user data for each email
      const promises = Array.from(allEmails).map(async (email) => {
        try {
          const user = await window.userSearch.findByEmail(email);
          if (user) {
            this.userCache.set(email, user);
          }
        } catch (error) {
          console.error(`Error fetching user for ${email}:`, error);
        }
      });

      await Promise.all(promises);
    },
    renderCommentText(text, highlightTerm = null) {
      if (!window.renderTextWithMentions) {
        return text; // Return plain text as fallback
      }
      return window.renderTextWithMentions(text, this.userCache, highlightTerm);
    },
    toggleResponseContextMenu() {
      this.responseContextMenuExpanded = !this.responseContextMenuExpanded;
    },
    collapseResponseContextMenu() {
      this.responseContextMenuExpanded = false;
    },
    startEditingResponse() {
      // Save text to allow reverting
      originalText = this.text;
      originalTextVisibleTo = this.text_visible_to;

      this.responseContextMenuExpanded = false;
      this.editingResponse = true;
    },
    stopEditingResponse() {
      this.editingResponse = false;

      // Revert to original text
      this.text = originalText;
      this.text_visible_to = originalTextVisibleTo;
    },
    updateResponse() {
      // Read the value from the hidden textarea (contains token format)
      const textarea = this.$refs.responseTextarea;
      const updatedText = textarea ? textarea.value : this.text;

      postData(baseUrl, {
        text: updatedText,
        text_visible_to: this.text_visible_to,
      });

      // Update the local text with the new value
      this.text = updatedText;
      this.editingResponse = false;
    },
    like() {
      postData(`${baseUrl}/like`);

      this.liked = true;
      this.like_count++;

      // Update the source response object so it persists across tab switches
      this.response.liked = true;
      this.response.like_count++;
    },
    unlike() {
      postData(`${baseUrl}/unlike`);

      this.liked = false;
      this.like_count--;

      // Update the source response object so it persists across tab switches
      this.response.liked = false;
      this.response.like_count--;
    },
    isAuthor(object, user) {
      return object.author.id == user.id;
    },
    expandComments() {
      this.commentsExpanded = true;
    },
    toggleCommentContextMenu(comment) {
      if (this.isCommentContextMenuExpanded(comment)) {
        this.commentContextMenuExpanded = false;
        return;
      }

      this.commentContextMenuExpanded = comment.id;
    },
    collapseCommentContextMenu(comment) {
      if (this.isCommentContextMenuExpanded(comment)) {
        this.commentContextMenuExpanded = false;
      }
    },
    isCommentContextMenuExpanded(comment) {
      return this.commentContextMenuExpanded == comment.id;
    },
    startEditingComment(comment) {
      this.collapseCommentContextMenu(comment);

      // Save original text to allow reverting
      originalCommentText = comment.text;

      this.editingComment = comment.id;
    },
    stopEditingComment(comment) {
      this.editingComment = false;

      // Revert to original text
      comment.text = originalCommentText;
    },
    isCommentBeingEdited(comment) {
      return this.editingComment == comment.id;
    },
    showNewCommentForm() {
      this.creatingComment = true;
    },
    hideNewCommentForm() {
      this.creatingComment = false;
    },
    async deleteComment(comment) {
      const { id } = comment;

      try {
        // Wait for the deletion to complete
        await postData("/snapshot_comment/delete", { id });

        // Only remove from collection after successful deletion
        this.comments = this.comments.filter((c) => c.id !== id);
      } catch (error) {
        console.error("Error deleting comment:", error);
      }
    },

    // Event handlers for commentEditor events
    async handleCommentSaved(event) {
      const { comment, mode, responseId } = event.detail;

      // Only handle events for this response
      if (responseId !== this.response.id) {
        return;
      }

      if (mode === "create-comment") {
        // Add new comment to the list
        this.comments.push(comment);

        // Hydrate user cache for any mentions in the new comment
        await this.hydrateUserCache();

        this.hideNewCommentForm();
        this.expandComments();
      } else if (mode === "edit-comment") {
        // Update existing comment in the list
        const existingComment = this.comments.find((c) => c.id === comment.id);
        if (existingComment) {
          existingComment.text = comment.text;
        }

        // Hydrate user cache in case mentions changed
        await this.hydrateUserCache();

        this.editingComment = false;
      }
    },

    handleCommentCancelled(event) {
      const { mode, responseId } = event.detail;

      // Only handle events for this response
      if (responseId !== this.response.id) {
        return;
      }

      if (mode === "create-comment") {
        this.hideNewCommentForm();
      } else if (mode === "edit-comment") {
        this.editingComment = false;
      } else if (mode === "edit-response") {
        this.editingResponse = false;
      }
    },

    async handleResponseSaved(event) {
      const { responseId, text } = event.detail;

      // Only handle events for this response
      if (responseId !== this.response.id) {
        return;
      }

      // Update the response text
      this.text = text;

      // Hydrate user cache for any mentions in the updated response
      await this.hydrateUserCache();

      // Close the editing mode
      this.editingResponse = false;
    },
  };
}
